import { Link, Redirect, useHistory, useParams } from "react-router-dom";
import { SmartApplyTemplate } from "../../../../template/smart-apply/default-template";
import {
  ContentsHeader,
  InformationBanner,
  ExternalLink,
  SecondaryButton,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
  Spinner,
  PrimaryButton,
} from "@graffer-inc/console-design-kit";
import { SmartApplyMenuList } from "../../../../organisms/smart-apply/menu-list";
import { SmartApplyR } from "../../route";
import { useModal } from "../../../../../utils/custom-hooks";
import { useApplicationRemovalTaskDetail } from "./hook";
import React, { useMemo } from "react";
import { ApplicationRemovalTaskStatusTag } from "../../../../molecules/application-removal-task/application-removal-task-status-tag";
import { DetailField } from "../../../../molecules/application-removal-task/application-removal-task-detail-field";
import { useAuthContextV2 } from "../../../../../auth-state";
import { toast } from "react-toastify";
import { BaseModal } from "@graffer-inc/smart-apply";
import {
  formatDatetime,
  formatOnlyDate,
} from "../../../../../utils/date-utils";
import { ApplicationRemovalTaskErrorDetailsLiterals } from "../../../../../application/domain/application/application-removal-task/application-removal-task";

export const ApplicationRemovalTaskDetail: React.FC = () => {
  const { auth } = useAuthContextV2();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const cancelModal = useModal();
  const { applicationRemovalTask, onCancel, isLoading } =
    useApplicationRemovalTaskDetail(id, auth);
  // 削除予定日前に手続きがグループ移動すると、申請が見れなくなることがある
  // ただ、運用上ではレアケースであり、申請IDのリンクが有効だったとしても、
  // 申請詳細は見れないので、上記のパターンは考えないことにする
  const isMemberOfGroup = useMemo(() => {
    if (auth.hasRole("Admin")) return true;

    return auth.groupIDs.includes(applicationRemovalTask?.groupId || "");
  }, [auth, applicationRemovalTask]);

  const resolveApplicationErrorMessage = (
    errDetail: ApplicationRemovalTaskErrorDetailsLiterals | string,
  ) => {
    if (errDetail === "application not found") {
      return "申請が見つかりませんでした。";
    }
    if (errDetail === "removed already") {
      return "申請はすでに削除されています。";
    }

    // 申請がない・申請が既に削除されているエラー以外の場合、テナントユーザーからできることはないため、特に詳細な内容は表示しない
    return "システムによる削除に失敗しました。サービス提供者により順次削除されます。";
  };

  if (
    !auth.checkGroupPermissionWithoutTarget(["applications.removal_tasks_read"])
  ) {
    toast.error("このページを閲覧する権限がありません。");
    return <Redirect to={SmartApplyR.Applications.link({})} />;
  }

  if (!applicationRemovalTask) {
    return null;
  }

  return (
    <SmartApplyTemplate
      breadcrumbs={[{ to: "#", title: "申請削除" }]}
      menuList={<SmartApplyMenuList />}
      contentsHeader={
        <ContentsHeader
          title={"申請削除タスク詳細"}
          caption={[
            <ApplicationRemovalTaskStatusTag
              key={"applicationRemovalTaskStatusTag"}
              status={applicationRemovalTask.status}
            />,
            <p key={"applicationRemovalTaskId"}>
              申請削除タスクID: {applicationRemovalTask.id}
            </p>,
          ]}
          buttons={[
            [
              <SecondaryButton
                key={"remove-application"}
                text={"一覧に戻る"}
                onClick={() => {
                  history.push(SmartApplyR.ApplicationRemovalTaskList.link({}));
                }}
              />,
            ],
          ]}
        />
      }
    >
      <div className="columns mb-0">
        <div className="column is-5">
          <dl className="is-horizontal">
            <DetailField label="削除予定日">
              {formatOnlyDate(
                applicationRemovalTask.scheduledRemoveUnixtime * 1000,
              )}
            </DetailField>
            <DetailField label="削除実行日時">
              {applicationRemovalTask.isExecuted() &&
              applicationRemovalTask.removalStartedAt
                ? formatDatetime(applicationRemovalTask.removalStartedAt)
                : "未削除"}
            </DetailField>
            <DetailField label="管理グループ">
              {applicationRemovalTask.groupName}
            </DetailField>
            <DetailField label="登録ユーザー名">
              {applicationRemovalTask.createdBy}
            </DetailField>
          </dl>
        </div>
        <div className="column is-7">
          <dl className="is-horizontal">
            <DetailField label="手続きID" labelWidth="160px">
              {applicationRemovalTask.procedureID}
            </DetailField>
            <DetailField label="手続き名" labelWidth="160px">
              {applicationRemovalTask.procedureName}
            </DetailField>
            <DetailField label="削除対象の申請日時" labelWidth="160px">
              {formatDatetime(applicationRemovalTask.earliestCreatedAt)}
              {" ~ "}
              {formatDatetime(applicationRemovalTask.latestCreatedAt)}
            </DetailField>
            <DetailField
              label="削除理由"
              labelWidth="160px"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {applicationRemovalTask.reason}
            </DetailField>
          </dl>
        </div>
      </div>
      {isLoading && <Spinner loading={isLoading} />}
      {applicationRemovalTask.status === "Scheduled" &&
        auth.checkGroupPermission(applicationRemovalTask, [
          "applications.removal_tasks_cancel",
        ]) && <SecondaryButton text={"中止する"} onClick={cancelModal.open} />}
      {/* TODO:申請一覧のページング実装 */}
      {applicationRemovalTask.status === "Error" &&
        applicationRemovalTask.errDetail && (
          <InformationBanner
            message={
              "申請削除が中断されました。サービス提供者により順次削除されます。"
            }
          />
        )}
      <Table wrapper={false} borderType="row">
        <TableHeader>
          <TableRow>
            <Th>申請ID</Th>
            <Th>申請の作成日時</Th>
            {applicationRemovalTask.isExecuted() && <Th>削除日時</Th>}
            {applicationRemovalTask.status === "Error" && <Th>エラー詳細</Th>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {applicationRemovalTask.targetApplications.map((application) => (
            <TableRow key={application.applicationID}>
              <Td>
                {applicationRemovalTask.status === "Scheduled" &&
                isMemberOfGroup ? (
                  <ExternalLink
                    anchor={({ ...props }) => (
                      <Link
                        {...props}
                        to={SmartApplyR.ApplicationDetail.link({
                          id: application.applicationID,
                        })}
                        className="p-0"
                      >
                        {application.applicationID}
                      </Link>
                    )}
                  >
                    <span>{application.applicationID}</span>
                  </ExternalLink>
                ) : (
                  application.applicationID
                )}
              </Td>
              <Td> {formatDatetime(application.createdAt)}</Td>
              {applicationRemovalTask.isExecuted() && (
                <Td>
                  {application.removedAt
                    ? formatDatetime(application.removedAt)
                    : "未削除"}
                </Td>
              )}
              {applicationRemovalTask.status === "Error" && (
                <Td>
                  {application.errDetail
                    ? resolveApplicationErrorMessage(application.errDetail)
                    : ""}
                </Td>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <BaseModal
        isActive={cancelModal.isActive}
        title={"申請削除タスクの中止"}
        onClose={cancelModal.close}
      >
        <section className="modal-card-body">
          <div className="is-small">
            <div>この申請削除タスクを中止します。</div>
            <div>中止すると、削除予定には戻せません。</div>
          </div>
        </section>
        <footer className="modal-card-foot is-justify-content-center">
          <div className="mx-4">
            <SecondaryButton
              key={"back"}
              text={"閉じる"}
              onClick={cancelModal.close}
            />
          </div>
          <div className="mx-4">
            <PrimaryButton
              key={"cancel"}
              text={"中止する"}
              onClick={() => {
                onCancel();
                cancelModal.close();
              }}
            />
          </div>
        </footer>
      </BaseModal>
    </SmartApplyTemplate>
  );
};
